import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pillar, Element, Character } from '../types';
import { pillarsAPI } from '../services/api';
import { initializeTabSwitching } from '../utils/tabSwitching';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { useOptimizedImage } from '../hooks/useOptimizedImage';
import { useDynamicFontSize } from '../hooks/useDynamicFontSize';
import ConfirmModal from './ConfirmModal';
import UploadImageModal from './UploadImageModal';
import SidePanel from './SidePanel';
import OptimizedImage from './OptimizedImage';
import imageLoader from '../utils/optimizedImageLoader';
import './PillarCard.css?v=2.0';
import { getImageUrl } from '../utils/imageUtils';

// Extend Window interface for global functions
declare global {
  interface Window {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }
}

interface PillarCardProps {
  pillar: Pillar;
  onUpdate: (pillar: Pillar) => void;
  onDelete: (pillarId: string) => void;
  onOpenSidePanel: (pillar: Pillar, field: string) => void;
  onOpenPreviewPanel: (elementIds: string[]) => void;
  defaultTab?: string;
}

const PillarCard: React.FC<PillarCardProps> = ({
  pillar,
  onUpdate,
  onDelete,
  onOpenSidePanel,
  onOpenPreviewPanel,
  defaultTab = 'article'
}) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showSaveCancel, setShowSaveCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteBtn, setShowDeleteBtn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [sidePanelField, setSidePanelField] = useState(defaultTab);
  const [sidePanelTitle, setSidePanelTitle] = useState('');
  
  // Card loading hook (now using useOptimizedImage with overlay)
  const imageSrc = getImageUrl(pillar.imageUrl, pillar.image, pillar._id, imageKey);
  const {
    cardRef,
    overlayRef,
    loadingState = { isLoading: false, progress: 0, error: false }
  } = useOptimizedImage({
    src: imageSrc,
    alt: pillar.name,
    withCardOverlay: true
  });
  
  const { modalState, showConfirm, handleConfirm, handleCancel: handleModalCancel } = useConfirmModal();
  const navigate = useNavigate();

  // Dynamic font size for title
  const titleRef = useDynamicFontSize<HTMLHeadingElement>({
    minFontSize: 1.5,
    maxFontSize: 5,
    step: 0.1,
    containerPadding: 40,
    maxHeight: 80
  });

  // Initialize tab switching when component mounts
  useEffect(() => {
    initializeTabSwitching(pillar._id);
  }, [pillar._id]);

  // Update sidePanelField when defaultTab prop changes
  useEffect(() => {
    setSidePanelField(defaultTab);
  }, [defaultTab]);

  const handleEdit = (field: string, value: string) => {
    console.log('🔄 handleEdit called:', { field, value });
    setEditingField(field);
    setEditValue(value);
    setShowSaveCancel(true);
  };

  const handleSave = async () => {
    if (!editingField) return;

    try {
      setLoading(true);
      console.log('🔄 Saving pillar field:', { field: editingField, value: editValue });
      console.log('🔄 Original pillar image:', pillar.image);
      console.log('🔄 Original pillar article:', pillar.article);
      
      const updateData = {
        [editingField]: editValue
      };
      console.log('🔄 Update data being sent:', updateData);
      
      const updatedPillar = await pillarsAPI.update(pillar._id, updateData);
      
      console.log('✅ Pillar updated successfully:', updatedPillar);
      console.log('🔄 Updated pillar image:', updatedPillar.image);
      console.log('🔄 Updated pillar imageUrl:', updatedPillar.imageUrl);
      console.log('🔄 Updated pillar article:', updatedPillar.article);
      
      // If the backend didn't return an imageUrl, preserve the existing one
      const finalPillar = {
        ...updatedPillar,
        imageUrl: updatedPillar.imageUrl || pillar.imageUrl
      };
      
      console.log('🔄 Final pillar with preserved imageUrl:', finalPillar.imageUrl);
      console.log('🔄 Final pillar article:', finalPillar.article);
      
      onUpdate(finalPillar);
      setEditingField(null);
      setShowSaveCancel(false);
      
      // Show success toast
      if (typeof window.showToast === 'function') {
        window.showToast('Pillar updated successfully!', 'success');
      }
    } catch (error: any) {
      console.error('❌ Failed to update pillar:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Show error toast
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update pillar. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setShowSaveCancel(false);
  };

  const handleDelete = async () => {
    // Use React confirm modal
    const confirmDelete = await showConfirm('Are you sure you want to delete this pillar?', { type: 'warning' });
    
    if (!confirmDelete) return;
    
    try {
      await pillarsAPI.delete(pillar._id);
      onDelete(pillar._id);
      // Show success toast if available
      if (typeof window.showToast === 'function') {
        window.showToast('Pillar deleted successfully!', 'success');
      }
    } catch (error) {
      console.error('Failed to delete pillar:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to delete pillar.', 'error');
      }
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please select an image file.', 'error');
      }
      return;
    }

    try {
      setUploading(true);
      
      // Create FormData
      const formData = new FormData();
      formData.append('image', file);
      formData.append('cardType', 'pillars');
      
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/pillars/${pillar._id}/image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Upload successful:', data);
      
      // Update the pillar with new image data
      console.log('🔄 Server response data:', data);
      
      // Extract the new image URL from the correct response path
      const newImageUrl = data.data?.imageUrl || data.data?.pillar?.imageUrl;
      const uploadTimestamp = data.data?.uploadTimestamp || Date.now();
      const newImage = data.data?.pillar?.image;
      
      console.log('🔄 Extracted data:', { newImageUrl, uploadTimestamp, newImage });
      
      if (newImageUrl) {
        console.log('🔄 Updating pillar with new image URL:', newImageUrl);
        const updatedPillar = { 
          ...pillar, 
          imageUrl: newImageUrl,
          image: newImage || pillar.image, // Also update the image key
          uploadTimestamp: uploadTimestamp // Include upload timestamp
        };
        onUpdate(updatedPillar);
        
        // Force image reload by generating new key
        const newKey = Date.now();
        console.log('🔄 Generating new image key:', newKey);
        setImageKey(newKey);
        setImageLoading(true);
        
        // Update the optimized image loader
        imageLoader.updateImage(pillar.imageUrl || '', newImageUrl);
        
        // Check if this is an L1 pillar and show notification about child updates
        const L1PillarCategories = [
          'Events and History', 
          'Geography and Places', 
          'Organizations and Societies',
          'Culture and Beliefs',
          'Technology and Creations',
          'Flora and Fauna',
          'Magic and Abilities',
          'Races and Species'
        ];
        
        const isL1Pillar = L1PillarCategories.includes(pillar.name);
        if (isL1Pillar) {
          if (typeof window.showToast === 'function') {
            window.showToast(`L1 pillar image updated! All child pillars of "${pillar.name}" have been automatically updated.`, 'success');
          }
        }
      } else {
        console.error('❌ No image URL in server response');
        console.error('❌ Response structure:', data);
      }
      
      if (typeof window.showToast === 'function') {
        window.showToast('Image updated successfully!', 'success');
      }
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to update image. Please try again.', 'error');
      }
      throw error; // Re-throw for modal to handle
    } finally {
      setUploading(false);
    }
  };

  const handleImageClick = () => {
    setDroppedFile(null);
    setShowUploadModal(true);
  };

  const handleOpenSidePanel = (field: string, title: string) => {
    setSidePanelField(field);
    setSidePanelTitle(title);
    setShowSidePanel(true);
  };

  const handleSidePanelClose = () => {
    setShowSidePanel(false);
    setSidePanelField('');
    setSidePanelTitle('');
  };

  // Drag and drop handlers for image upload
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    
    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      if (typeof window.showToast === 'function') {
        window.showToast('Please drop an image file.', 'error');
      }
      return;
    }
    
    // Set the dropped file and open upload modal
    setDroppedFile(file);
    setShowUploadModal(true);
  };

  const renderArrayField = (field: string, items: (Element | Character)[], title: string) => (
    <div className="card-array__container">
      <button
        className="card-array__search-btn"
        type="button"
        aria-label={`Update ${title}`}
        onClick={() => handleOpenSidePanel(field, title)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="darkorchid">
          <use xlinkHref="/img/icons.svg#icon-edit" />
        </svg>
        <span className="card-array__badge">{items.length}</span>
      </button>
      <div className="card-array__title-container">
        <h4 className="card-array__title--small">{title}</h4>
        {items && items.length > 0 ? (
          <button
            className="element-preview-btn"
            type="button"
            onClick={() => onOpenPreviewPanel(items.map(item => item._id))}
          >
            {items[0].name}
          </button>
        ) : (
          <span className="no-elements">NONE</span>
        )}
      </div>
    </div>
  );

  return (
    <div 
      className="pillar-card__container" 
      data-pillar-id={pillar._id}
      ref={cardRef}
      onMouseEnter={() => setShowDeleteBtn(true)}
      onMouseLeave={() => setShowDeleteBtn(false)}
      style={{ position: 'relative' }}
    >
      {/* Card loading overlay */}
      <div ref={overlayRef} />
      {/* Delete button */}
      {showDeleteBtn && (
        <button 
          className="card__delete-btn" 
          onClick={handleDelete}
          title="Delete pillar"
        >
          ×
        </button>
      )}

      {/* Left Section: Pillar Image and L1 Pillar */}
      <div className="pillar-card__left">
        <div className="pillar-card__image-wrapper"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: isDragging ? '3px dashed #55c57a' : '',
            backgroundColor: isDragging ? 'rgba(85, 197, 122, 0.1)' : ''
          }}
        >
          <OptimizedImage
            src={getImageUrl(pillar.imageUrl, pillar.image, pillar._id, imageKey)}
            alt={pillar.name}
            className="pillar-card__main-image"
            style={{
              opacity: uploading || imageLoading ? 0.5 : 1,
              filter: uploading || imageLoading ? 'grayscale(50%)' : 'none',
              cursor: 'pointer',              
            }}
            onClick={handleImageClick}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
          />
          {(loadingState?.isLoading ?? false) && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, width: '100%', height: '100%',
              background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.75) 0%, rgba(22, 33, 62, 0.75) 50%, rgba(15, 52, 96, 0.75) 100%)',
              zIndex: 99999,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              // border: '2.5px solid',
              // borderImage: 'linear-gradient(90deg, #7fbcff 0%, #b388ff 100%) 1',
              borderRadius: 'inherit',
              backdropFilter: 'blur(2px)'
            }}>
              <img src="/img/AeLogo.ico" alt="Loading..." style={{ width: 50, height: 50, marginBottom: 16, animation: 'cardBounce 1.2s ease-in-out infinite, cardGlow 2s ease-in-out infinite' }} />
              <span style={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.7)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}>Loading...</span>
            </div>
          )}
          {/* Drag overlay */}
          <div 
            className="drag-overlay"
            style={{
              opacity: isDragging ? 1 : 0,
              borderColor: isDragging ? '#55c57a' : 'transparent'
            }}
          >
            <span style={{ color: '#55c57a', fontWeight: 'bold', fontSize: '0.9rem' }}>
              Drop image here
            </span>
          </div>
        </div>
        
        {/* L1 Pillar Image - moved outside image wrapper */}
        {pillar.L1pillarImageUrl ? (
          <div className="pillar-card__l1-image-container">
            <img
              src={pillar.L1pillarImageUrl}
              alt={`L1 Pillar: ${pillar.L1pillar || 'Unknown'}`}
              className="pillar-card__l1-image"             
              onError={async (e) => {
                console.error('❌ L1 pillar image failed to load:', pillar.L1pillarImageUrl);
                // Check if the element is still valid
                if (!e.currentTarget) {
                  console.error('❌ Cannot set properties of null (setting src) - element is null');
                  return;
                }
                
                // Try to get a signed URL for the default image from the backend
                try {
                  const token = localStorage.getItem('token');
                  const headers: Record<string, string> = {};
                  if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                  }

                  const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/pillars/default-image`, {
                    method: 'GET',
                    credentials: 'include',
                    headers
                  });
                  if (response.ok) {
                    const data = await response.json();
                    if (e.currentTarget && e.currentTarget.src !== undefined) {
                      e.currentTarget.src = data.imageUrl;
                    }
                  } else {
                    // Fallback to hardcoded default
                    if (e.currentTarget && e.currentTarget.src !== undefined) {
                      e.currentTarget.src = 'https://aethermap-images.s3.amazonaws.com/pillars/default-0.jpg';
                    }
                  }
                } catch (error) {
                  console.error('❌ Failed to get default image URL:', error);
                  // Final fallback
                  if (e.currentTarget && e.currentTarget.src !== undefined) {
                    e.currentTarget.src = 'https://aethermap-images.s3.amazonaws.com/pillars/default-0.jpg';
                  }
                }
              }}
              onLoad={() => {
                console.log('✅ L1 pillar image loaded successfully:', pillar.L1pillarImageUrl);
              }}
            />
            <div className="pillar-card__l1-name-overlay">
              <span>{pillar.L1pillar || 'Unknown L1'}</span>
            </div>
          </div>             
        ) : (
          <div className="pillar-card__l1-image-container">
            <span>No L1 Image</span>
          </div>
        )}
      </div>

             {/* Right Section: Tabs */}
       <div className="pillar-card__right">
         <h2 
           className="pillar-card__title" 
           ref={titleRef}
         >
           {pillar.name}
         </h2>
         <p className="pillar-card__level">Pillar Level {pillar.levelFromL1 || pillar.level || 1}</p>
         
         {/* Hierarchy button for L1 pillars */}
         {pillar.L1pillar === pillar.name && (
           <button 
             className="pillar-card__hierarchy-btn"
             onClick={() => navigate(`/pillars/hierarchy/${encodeURIComponent(pillar.name)}`)}
             title="View full hierarchy"
           >
             🌳 View Hierarchy
           </button>
         )}
         
         <div className="pillar-card__tabs">
           <button className={`pillar-card__tab-btn${sidePanelField === 'article' ? ' active' : ''}`} onClick={() => setSidePanelField('article')}>Article</button>
                       <button className={`pillar-card__tab-btn${sidePanelField === 'ancestors' ? ' active' : ''}`} onClick={() => setSidePanelField('ancestors')}>Ancestors</button>
           <button className={`pillar-card__tab-btn${sidePanelField === 'children' ? ' active' : ''}`} onClick={() => setSidePanelField('children')}>Children</button>
           <button className={`pillar-card__tab-btn${sidePanelField === 'elements' ? ' active' : ''}`} onClick={() => setSidePanelField('elements')}>Elements</button>
         </div>
                   <div className="pillar-card__tab-content">
            {sidePanelField === 'article' && (
              <div>
                <h4>Article</h4>
                <div 
                  className={`article-content ${editingField === 'article' ? 'editing' : ''}`}
                  onClick={() => {
                    if (editingField !== 'article') {
                      handleEdit('article', pillar.article || '');
                      // Move cursor to end of text
                      setTimeout(() => {
                        const element = document.querySelector('.article-content') as HTMLElement;
                        if (element) {
                          const range = document.createRange();
                          const selection = window.getSelection();
                          range.selectNodeContents(element);
                          range.collapse(false);
                          selection?.removeAllRanges();
                          selection?.addRange(range);
                        }
                      }, 0);
                    }
                  }}
                  contentEditable={editingField === 'article'}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    if (editingField === 'article') {
                      const newValue = e.currentTarget.textContent || '';
                      setEditValue(newValue);
                    }
                  }}
                  onBlur={(e) => {
                    if (editingField === 'article') {
                      const newValue = e.currentTarget.textContent || '';
                      if (newValue !== pillar.article) {
                        setEditValue(newValue);
                        // Don't auto-save on blur, let user click save button
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (editingField === 'article') {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        const newValue = e.currentTarget.textContent || '';
                        if (newValue !== pillar.article) {
                          setEditValue(newValue);
                          handleSave();
                        } else {
                          handleCancel();
                        }
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        handleCancel();
                      }
                    }
                  }}
                  style={{
                    minHeight: '4rem',
                    padding: '0.6rem',
                    border: editingField === 'article' ? '2px solid #1d87ff' : '1px solid #ccc',
                    borderRadius: '0.3rem',
                    backgroundColor: editingField === 'article' ? 'rgba(85, 197, 122, 0.1)' : '#fff',
                    cursor: editingField === 'article' ? 'text' : 'pointer',
                    outline: 'none',
                    fontFamily: 'inherit',
                    fontSize: '1.1rem',
                    lineHeight: '1.4',
                    color: '#777',
                    whiteSpace: 'pre-wrap',
                    transition: 'all 0.2s'
                  }}
                >
                  {pillar.article || 'Click to edit article content...'}
                </div>
                {editingField === 'article' && (
                  <div className="inline-edit-buttons" style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginTop: '0.5rem',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      style={{
                        background: loading ? '#ccc' : '#2196f3',
                        color: 'white',
                        border: 'none',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={loading}
                      style={{
                        background: loading ? '#ccc' : '#666',
                        color: 'white',
                        border: 'none',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
                                                {sidePanelField === 'ancestors' && (
                   <div>
                     <h4>Ancestors</h4>
                     <div className="chips-container">
                       {pillar.ancestors && pillar.ancestors.length > 0 ? (
                         pillar.ancestors.map((ancestor) => (
                           <div key={ancestor._id} className="chip">
                             <span className="chip-level">L{ancestor.level}</span>
                             <span className="chip-name">{ancestor.name}</span>
                           </div>
                         ))
                       ) : (
                         <div className="chip chip-empty">None</div>
                       )}
                     </div>
                   </div>
                 )}
                 {sidePanelField === 'children' && (
                   <div>
                     <h4>Children</h4>
                     <div className="chips-container">
                       {pillar.children && pillar.children.length > 0 ? (
                         pillar.children.map((child) => (
                           <div key={child._id} className="chip">
                             <span className="chip-level">L{child.level}</span>
                             <span className="chip-name">{child.name}</span>
                           </div>
                         ))
                       ) : (
                         <div className="chip chip-empty">None</div>
                       )}
                     </div>
                   </div>
                 )}
                           {sidePanelField === 'elements' && (
                   <div>
                     <h4>Elements</h4>
                     <div className="chips-container">
                       {pillar.elements && pillar.elements.length > 0 ? (
                         pillar.elements.map((element: any) => (
                           <div key={element._id || element.name} className="chip">
                             <span className="chip-name">{element.name}</span>
                           </div>
                         ))
                       ) : (
                         <div className="chip chip-empty">None</div>
                       )}
                     </div>
                   </div>
                 )}
        </div>
        <div className="pillar-card__details-btn-wrapper">
          <Link className="pillar-card__details-btn" to={`/pillar/${pillar.slug}`}>Details</Link>
        </div>
      </div>

      {/* Confirm Modal */}
      {modalState && (
        <ConfirmModal
          isOpen={modalState.isOpen}
          message={modalState.message}
          type={modalState.type}
          okText={modalState.okText}
          cancelText={modalState.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleModalCancel}
        />
      )}

      {/* Upload Image Modal */}
      <UploadImageModal
        isOpen={showUploadModal}
        characterName={pillar.name}
        initialFile={droppedFile || undefined}
        onUpload={handleImageUpload}
        onCancel={() => {
          setShowUploadModal(false);
          setDroppedFile(null);
        }}
      />

      {/* Side Panel */}
      <SidePanel
        isOpen={showSidePanel}
        onClose={handleSidePanelClose}
        entity={pillar}
        field={sidePanelField}
        title={sidePanelTitle}
        onUpdate={(updatedEntity) => onUpdate(updatedEntity as Pillar)}
      />
    </div>
  );
};

export default PillarCard; 